import { Popover, Skeleton } from "antd";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";

import TableNoContent from "@/Components/common/TableNoContent";
import {
  fetchCandidateDocuments,
  setLoading,
  updateSelectedDocuments,
  openCandidateComplianceDoc,
} from "@/reducers/clientcandidatedetail";
import { getColumnNormalHtml } from "@/utilities/datatable/columnFunctions";
import { sortByDate, sortByText } from "@/utilities/DataTableSorting";

const ComplianceListing = () => {
  const {
    candidateDetails,
    isLoading,
    candidateDocuments,
    activeComplianceTab,
    complianceFilters,
    toggleListingSelection,
  } = useSelector((state) => state.clientCandidateDetail);
  const { user } = useSelector((state) => state.clientProfile);
  const { clientUserAllPermissions } = useSelector((state) => state.session);
  const [data, setData] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (candidateDetails && candidateDetails.candidate_id) {
      dispatch(setLoading(true));
      dispatch(
        fetchCandidateDocuments({
          candidate_id: candidateDetails.candidate_id,
        }),
      );
    }
  }, [candidateDetails]);

  useEffect(() => {
    if (candidateDocuments && candidateDocuments[activeComplianceTab]) {
      setData(candidateDocuments[activeComplianceTab]);
    }
  }, [candidateDocuments, activeComplianceTab]);
  /**
   * Get the status element based on the status title.
   *
   * @param {string} statusTitle - the title of the status
   * @return {JSX.Element} the status element based on the title
   */
  const getStatus = (statusTitle) => {
    let status = statusTitle.toLowerCase();
    if (status === "approved") {
      return (
        <span className="h-[20px] max-w-[95px] rounded-[5px] bg-[#DFF1DE] px-2 text-center text-[12px] text-green">
          {statusTitle}
        </span>
      );
    } else if (status === "not uploaded" || status === "rejected") {
      return (
        <span className="h-[20px] max-w-[105px] rounded-[5px] bg-red-100 px-2 text-center text-[12px] text-red">
          {statusTitle}
        </span>
      );
    } else if (status === "not applicable") {
      return (
        <span className="h-[20px] min-w-[75px] rounded-[5px] bg-[#FFF4DC] px-2 text-center text-[12px] text-yellow">
          {statusTitle}
        </span>
      );
    } else if (status === "awaiting approval") {
      return (
        <span className="h-[20px] max-w-[120px] rounded-[5px] bg-[#FFF4DC] px-2 text-center text-[12px] text-yellow">
          {statusTitle}
        </span>
      );
    }
  };
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      dispatch(setLoading(true));
      dispatch(
        fetchCandidateDocuments({
          candidate_id: candidateDetails.candidate_id,
        }),
      );
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [complianceFilters.search]);

  const handleFetchCandidateComplianceDoc = (row, attachmentId = "-") => {
    dispatch(
      openCandidateComplianceDoc({
        candidate_id: candidateDetails.candidate_id,
        document_id: row?.document_id,
        attachment_id: attachmentId || "-",
      }),
    );
  };

  const columns = [
    {
      name: "Title",
      sortable: true,
      sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "name"),
      selector: (row) => {
        const canViewCompliance =
          !clientUserAllPermissions ||
          clientUserAllPermissions?.is_allow_to_view_download_compliance;
        const hasDocuments =
          Array?.isArray(row.document_url) && row.document_url?.length > 0;

        if (!canViewCompliance) {
          return (
            <span className="text-[14px] font-medium leading-[normal] text-primary hover:text-black">
              {row.name}
            </span>
          );
        }

        if (!hasDocuments) {
          return (
            <span className="text-[14px] font-medium leading-[normal] text-primary hover:text-primaryHover">
              {row.name}
            </span>
          );
        }

        if (row.document_url.length > 1) {
          return (
            <Popover
              content={
                <div className="flex flex-col">
                  {row.document_url.map((doc, index) => (
                    <button
                      key={index}
                      onClick={() =>
                        handleFetchCandidateComplianceDoc(
                          row,
                          doc?.attachment_id,
                        )
                      }
                      className="border-b border-primary-100 px-7 py-3 text-[14px] text-primary last:border-b-0 hover:bg-lightblue hover:text-primaryHover"
                    >
                      Attachment {index + 1}
                    </button>
                  ))}
                </div>
              }
              trigger="hover"
              placement="right"
            >
              <button className="group flex items-center gap-2 text-[14px] font-medium leading-[normal] text-primary hover:text-primaryHover">
                {row.name}
                <span className="transition-linear rounded-md bg-lightblue px-2 py-1 text-center text-[14px] text-primary group-hover:bg-primary group-hover:text-white">
                  {row.document_url.length}
                </span>
              </button>
            </Popover>
          );
        }

        return (
          <button
            onClick={() =>
              handleFetchCandidateComplianceDoc(
                row,
                row.document_url[0]?.attachment_id,
              )
            }
            className="text-[14px] font-medium leading-[normal] text-primary hover:text-primaryHover"
          >
            {row.name}
          </button>
        );
      },
      width: "32%",
    },

    {
      name: "Uploaded",
      sortable: true,
      sortFunction: (rowA, rowB) =>
        sortByDate(
          moment(rowA.uploaded_date, "DD/MM/YYYY hh:mm").format("DD/MM/YYYY"),
          moment(rowB.uploaded_date, "DD/MM/YYYY hh:mm").format("DD/MM/YYYY"),
        ),
      selector: (row) => (
        <div className="grid gap-2">
          {getColumnNormalHtml(row.uploaded_date)}
          {row.status_title ? getStatus(row.status_title) : null}
        </div>
      ),
    },
    {
      name: "Approved By",
      sortable: false,
      selector: (row) => (
        <div className="flex flex-col items-start gap-1">
          {row.approved_by && row.approved_by !== "-"
            ? getColumnNormalHtml(row.approved_by)
            : null}
        </div>
      ),
    },
    {
      name: "Date Of Update",
      sortable: true,
      sortFunction: (rowA, rowB) =>
        sortByDate(
          moment(rowA.updated_date, "DD/MM/YYYY hh:mm").format("DD/MM/YYYY"),
          moment(rowB.updated_date, "DD/MM/YYYY hh:mm").format("DD/MM/YYYY"),
        ),
      selector: (row) =>
        getColumnNormalHtml(row.updated_date ? row.updated_date : "-"),
    },
    {
      name: "Expiry Date",
      sortable: true,
      sortFunction: (rowA, rowB) =>
        sortByDate(
          moment(rowA.expiry_date, "DD/MM/YYYY hh:mm").format("DD/MM/YYYY"),
          moment(rowB.expiry_date, "DD/MM/YYYY hh:mm").format("DD/MM/YYYY"),
        ),
      selector: (row) => getColumnNormalHtml(row.expiry_date),
    },
  ];
  const wrapper = useRef(null);
  let height = wrapper?.current?.clientHeight
    ? wrapper?.current?.clientHeight - 66
    : 400;
  const dataTableHeight = `${height}px`;
  return (
    <div
      className="ComplianceListing_Wrapper DataTable_Wrapper flex-1"
      ref={wrapper}
    >
      <DataTable
        columns={columns}
        data={data}
        selectableRows
        clearSelectedRows={toggleListingSelection}
        onSelectedRowsChange={(state) => {
          let selectedDocuments = state.selectedRows.map((item) => {
            return item.document_id;
          });
          dispatch(updateSelectedDocuments(selectedDocuments));
        }}
        noDataComponent={<TableNoContent />}
        progressPending={isLoading}
        selectableRowDisabled={(row) => {
          return (
            user?.is_bank_client === 0 &&
            candidateDetails?.is_bank_candidate === 0 &&
            row.status_title === "Not Uploaded"
          );
        }}
        progressComponent={<Skeleton active paragraph={{ rows: 1 }} />}
        fixedHeader
        fixedHeaderScrollHeight={dataTableHeight}
      />
    </div>
  );
};

export default ComplianceListing;
